import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import ThickSlider from "./ThickSlider";
import { HB_FS, HB_B1, HB_B2, HB_WN } from "../heartbeatBumps";

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_S = 0.12;

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

function int16ToAudioBuffer(ctx, int16Arr, fs) {
  const buf = ctx.createBuffer(1, int16Arr.length, fs);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < int16Arr.length; i++) ch[i] = int16Arr[i] / 32768;
  return buf;
}

const HeartbeatPlayerEngine = forwardRef(function HeartbeatPlayerEngine(
  { bpm, amplitude, onBpmChange, onAmplitudeChange },
  ref
) {
  // Refs to avoid stale closures
  const bpmRef = useRef(bpm);
  useEffect(() => { bpmRef.current = bpm; }, [bpm]);

  // WebAudio refs
  const ctxRef = useRef(null);
  const masterGainRef = useRef(null);
  const b1BufRef = useRef(null);
  const b2BufRef = useRef(null);

  // Scheduler refs
  const timerRef = useRef(null);
  const nextBeatTimeRef = useRef(0);

  const gapCacheRef = useRef(new Map());

  const masterGainValue = useMemo(() => {
    const pct = clamp(amplitude, 1, 100) / 100;
    return clamp(Math.pow(pct, 1.3) * 0.9, 0.001, 0.9);
  }, [amplitude]);

  function ensureAudio() {
    if (ctxRef.current) return;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();

    const master = ctx.createGain();
    master.gain.value = 0.0;
    master.connect(ctx.destination);

    b1BufRef.current = int16ToAudioBuffer(ctx, HB_B1, HB_FS);
    b2BufRef.current = int16ToAudioBuffer(ctx, HB_B2, HB_FS);

    ctxRef.current = ctx;
    masterGainRef.current = master;
  }

  function makeFirmwareGapBuffer(ctx, gapSamples16k, noiseScale = 1.0) {
    const n = Math.max(0, Math.floor(gapSamples16k));
    const buf = ctx.createBuffer(1, n, HB_FS);
    const ch = buf.getChannelData(0);

    for (let i = 0; i < n; i++) {
      let smoothCoef = 1.0;
      const remaining = (n - 1) - i;
      if (remaining < 50) smoothCoef = remaining / 50.0;

      const s16 = HB_WN[i % HB_WN.length];
      ch[i] = (s16 / 32768) * smoothCoef * noiseScale;
    }
    return buf;
  }

  function scheduleGap(time, gapSamples16k) {
    const ctx = ctxRef.current;
    const master = masterGainRef.current;
    if (!ctx || !master || gapSamples16k <= 0) return;

    let buf = gapCacheRef.current.get(gapSamples16k);
    if (!buf) {
      buf = makeFirmwareGapBuffer(ctx, gapSamples16k, 0.9);
      gapCacheRef.current.set(gapSamples16k, buf);
    }

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(master);
    src.start(time);
    src.stop(time + buf.duration + 0.01);
  }

  function scheduleBump(time, which) {
    const ctx = ctxRef.current;
    const master = masterGainRef.current;
    const b1 = b1BufRef.current;
    const b2 = b2BufRef.current;
    if (!ctx || !master || !b1 || !b2) return;

    const src = ctx.createBufferSource();
    src.buffer = which === "lub" ? b1 : b2;

    // Tiny fade to prevent edge clicks
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0, time);

    const fade = 0.004;
    const end = time + src.buffer.duration;

    g.gain.linearRampToValueAtTime(1.0, time + fade);
    g.gain.setValueAtTime(1.0, Math.max(time + fade, end - fade));
    g.gain.linearRampToValueAtTime(0.0, end);

    src.connect(g);
    g.connect(master);

    src.start(time);
    src.stop(end + 0.01);
  }

  function schedulerTick() {
    const ctx = ctxRef.current;
    const b1 = b1BufRef.current;
    const b2 = b2BufRef.current;
    if (!ctx || !b1 || !b2) return;

    const bpmNow = clamp(bpmRef.current, 30, 200);
    const beatDur = 60 / bpmNow;

    // Reference-like lub->dub spacing
    const dubOffset = clamp(beatDur * 0.375, 0.18, 0.55);

    const b1Dur = b1.duration;
    const b2Dur = b2.duration;

    const now = ctx.currentTime;
    while (nextBeatTimeRef.current < now + SCHEDULE_AHEAD_S) {
      const t0 = nextBeatTimeRef.current;
      const dubTime = t0 + dubOffset;

      const gapB1B2 = Math.max(0, dubTime - (t0 + b1Dur));
      const gapB2B1 = Math.max(0, (t0 + beatDur) - (dubTime + b2Dur));

      scheduleBump(t0, "lub");
      scheduleGap(t0 + b1Dur, Math.round(gapB1B2 * HB_FS));

      scheduleBump(dubTime, "dub");
      scheduleGap(dubTime + b2Dur, Math.round(gapB2B1 * HB_FS));

      nextBeatTimeRef.current += beatDur;
    }
  }

  async function play() {
    ensureAudio();
    const ctx = ctxRef.current;
    const master = masterGainRef.current;
    if (!ctx || !master) return;

    if (ctx.state === "suspended") await ctx.resume();

    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(masterGainValue, now + 0.05);

    nextBeatTimeRef.current = now + 0.05;

    if (!timerRef.current) {
      timerRef.current = window.setInterval(schedulerTick, LOOKAHEAD_MS);
    }
  }

  function stop() {
    const ctx = ctxRef.current;
    const master = masterGainRef.current;

    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (ctx && master) {
      const now = ctx.currentTime;
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(master.gain.value, now);
      master.gain.linearRampToValueAtTime(0.0, now + 0.06);
    }
  }

  // keep gain synced while playing
  useEffect(() => {
    const ctx = ctxRef.current;
    const master = masterGainRef.current;
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(masterGainValue, now + 0.05);
  }, [masterGainValue]);

  // expose controls to parent
  useImperativeHandle(ref, () => ({
    play,
    stop,
  }));

  // cleanup
  useEffect(() => {
    return () => {
      stop();
      const ctx = ctxRef.current;
      if (ctx) {
        try { ctx.close(); } catch {}
      }
      ctxRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="heartbeat-player-engine">
      <ThickSlider label="Heartbeats Per Minute" value={bpm} min={30} max={150} onChange={onBpmChange} />
      <ThickSlider label="Heartbeat Strength" value={amplitude} min={1} max={100} onChange={onAmplitudeChange} />
    </div>
  );
});

export default HeartbeatPlayerEngine;
